import { useI18n } from "@wato/i18n/react";
import type { MessageKey } from "@wato/i18n";

/** Weekday keys of `peakHours.day.*`, index 0 = Sunday. */
export const PEAK_DAY_KEYS = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"] as const;
const HOURS = Array.from({ length: 24 }, (_, i) => i);

/**
 * The weekday x hour heatmap table shared by the results dashboard (OWN-53) and the owner overview
 * (OWN-164). `grid[weekday][hour]` is the count (weekday 0 = Sunday); `max` is the busiest cell.
 * Callers wrap it in an `overflow-x-auto` box. `caption` is an optional screen-reader text alternative.
 */
export function PeakHoursGrid({
  grid,
  max,
  caption,
}: {
  readonly grid: readonly (readonly number[])[];
  readonly max: number;
  readonly caption?: string | undefined;
}) {
  const { t, locale } = useI18n();
  return (
    <table className="border-separate border-spacing-0.5 text-xs">
      {caption ? <caption className="sr-only">{caption}</caption> : null}
      <thead>
        <tr>
          <th />
          {HOURS.map((h) => (
            <th key={h} scope="col" className="px-0.5 text-center font-normal text-fg-muted">
              {h}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {grid.map((row, d) => (
          <tr key={d}>
            <th scope="row" className="pe-2 text-start font-medium text-fg-muted">
              {t(`peakHours.day.${PEAK_DAY_KEYS[d]}` as MessageKey)}
            </th>
            {row.map((n, h) => (
              <td
                key={h}
                title={t("peakHours.cell" as MessageKey, {
                  count: n.toLocaleString(locale),
                  hour: h,
                })}
                className="size-5 min-w-5 rounded-sm bg-surface-muted"
                style={{
                  // Solid fill: the share of the busiest hour, mixed into the cell colour.
                  backgroundColor: `color-mix(in srgb, var(--color-action-primary) ${
                    max ? Math.round((n / max) * 100) : 0
                  }%, var(--color-surface-muted))`,
                }}
              />
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
}
